/**
 * Insights endpoints (GET /admin/analytics/{scorecard,funnel2,outreachers,
 * spend,sources,quality}), per the reporting contract of 2026-10-02.
 *
 * Every response carries `definitions`: one plain-English line per metric
 * key, written next to the SQL, which the UI shows as the metric's tooltip.
 * Money is a 2-decimal string like the rest of the API; counts are ints;
 * rates are 0..1, or null when the denominator is 0 (or the team's joins
 * aren't tracked). Money is typed `string | number` so a computed field
 * that arrives as a number still renders.
 *
 * Kept out of types.ts so the reporting rebuild doesn't collide with other
 * work on the shared types file.
 */

export type Money = string | number;
export type Rate = number | null;
export type Definitions = Record<string, string>;

/** Half-open [from, to), ISO timestamps. */
export interface InsightsWindow {
  from: string;
  to: string;
}

// ── GET /scorecard ──────────────────────────────────────────────────────────

export interface ScorecardMetrics {
  messages: number;
  replied: number;
  /** Replies on tracked teams: the join / post rate denominator. */
  repliedTracked: number;
  repliedAfterMessage: number;
  /** repliedAfterMessage ÷ messages. */
  replyRate: Rate;
  joinedCohort: number;
  joinedCohortTracked: number;
  joinRate: Rate;
  postedCohort: number;
  postedCohortTracked: number;
  postRate: Rate;
  joinedActivity: number;
  postedActivity: number;
  cashSpent: Money;
  inviteEarningsPaid: Money;
  additionalPaid: Money;
  costPerJoin: Money | null;
  costPerPost: Money | null;
  /** Ledger owed now (invite_owed_v): positives only. Not Payroll's owed figure. */
  owedNow: Money;
  /** Outreachers with something owed now. */
  owedOutreachers: number;
  /** Credit (overpaid) now, reported apart from owedNow rather than netted. */
  creditNow: Money;
  /** Current earned value of the replies in the window. */
  earned: Money;
}

export interface ScorecardWeek {
  /** Monday of the ISO week (date or ISO timestamp). */
  weekStart: string;
  messages: number;
  replied: number;
  joinedActivity: number;
  postedActivity: number;
  cashSpent: Money;
}

export interface ScorecardTeamRow extends ScorecardMetrics {
  team: string;
  teamDisplayName: string | null;
  /** clipping_resolve_required: joins and posts are tracked automatically. */
  tracked: boolean;
}

export interface ScorecardResponse {
  window: InsightsWindow;
  previous: InsightsWindow;
  current: ScorecardMetrics;
  prior: ScorecardMetrics;
  weekly: ScorecardWeek[];
  byTeam: ScorecardTeamRow[];
  definitions: Definitions;
}

// ── GET /funnel2 ────────────────────────────────────────────────────────────

export type FunnelMode = 'cohort' | 'activity';
export type FunnelStageKey = 'messaged' | 'replied' | 'joined' | 'posted';

export interface FunnelStage {
  key: FunnelStageKey;
  count: number;
  rateFromPrevious: Rate;
  rateFromStart: Rate;
}

export interface DurationStats {
  p50: number | null;
  p90: number | null;
  /** How many creators the percentiles are taken over. */
  n: number;
}

export interface FunnelChannelRow {
  channel: string;
  messaged: number;
  replied: number;
  joined: number;
  posted: number;
}

export interface FunnelCreatorTypeRow {
  creatorType: string;
  replied: number;
  joined: number;
  posted: number;
  joinRate: Rate;
  postRate: Rate;
}

export interface Funnel2Response {
  window: InsightsWindow;
  mode: FunnelMode;
  channel: string | null;
  stages: FunnelStage[];
  timeToJoinDays: DurationStats;
  timeToPostDays: DurationStats;
  byChannel: FunnelChannelRow[];
  byCreatorType: FunnelCreatorTypeRow[];
  excludedUntrackedTeams: string[];
  definitions: Definitions;
}

// ── GET /outreachers ────────────────────────────────────────────────────────

export interface OutreacherInsightRow {
  inviterId: number;
  publicId: string;
  name: string;
  /** The outreacher's current team. */
  team: string;
  isActive: boolean;
  messages: number;
  replied: number;
  repliedAfterMessage: number;
  replyRate: Rate;
  joinedCohort: number;
  postedCohort: number;
  joinRate: Rate;
  postRate: Rate;
  joinedActivity: number;
  postedActivity: number;
  cashPaid: Money;
  additionalPaid: Money;
  earned: Money;
  owedNow: Money;
  costPerPost: Money | null;
  proofReviewed: number;
  proofOk: number;
  proofFail: number;
  proofUnclear: number;
  proofFailRate: Rate;
  duplicateCreators: number;
  lastLoggedAt: string | null;
}

export interface OutreachersResponse {
  window: InsightsWindow;
  rows: OutreacherInsightRow[];
  /** The rows summed. */
  totals: ScorecardMetrics;
  definitions: Definitions;
}

// ── GET /spend ──────────────────────────────────────────────────────────────

export type EarnedComponent = 'log' | 'join' | 'post' | 'bracket' | 'niche' | 'other';
export type AgingBucket = '0-7' | '8-30' | '31-90' | '90+';

/** costPer rows for cash not tied to any invite (prizes, retainers, top-ups). */
export const UNATTRIBUTED = '(unattributed)';
/** costPer.byNiche row for invites with no niche. */
export const NO_NICHE = '(none)';

export interface CostPerRow {
  cash: Money;
  joined: number;
  posted: number;
  costPerJoin: Money | null;
  costPerPost: Money | null;
}

export interface SpendResponse {
  window: InsightsWindow;
  cash: { total: Money; inviteEarnings: Money; additional: Money; beforeTrackingAllTime: Money };
  weekly: { weekStart: string; inviteEarnings: Money; additional: Money }[];
  earnedByComponent: { component: EarnedComponent; amount: Money }[];
  costPer: {
    byTeam: (CostPerRow & { team: string })[];
    byCreatorType: (CostPerRow & { creatorType: string })[];
    byNiche: (CostPerRow & { niche: string })[];
  };
  liabilityAging: { bucket: AgingBucket; owed: Money; invites: number }[];
  definitions: Definitions;
}

// ── GET /sources ────────────────────────────────────────────────────────────

export interface SourceFolderRow {
  folderPublicId: string | null;
  name: string | null;
  exported: number;
  messaged: number;
  replied: number;
  joined: number;
  posted: number;
  cashPaid: Money;
  joinRate: Rate;
  postRate: Rate;
}

export interface SourceNicheRow {
  niche: string;
  replied: number;
  joined: number;
  posted: number;
  joinRate: Rate;
  postRate: Rate;
  earned: Money;
}

export interface SourcesResponse {
  window: InsightsWindow;
  folders: SourceFolderRow[];
  /** Valid invites in the window with no discovery lead behind them. */
  unlinkedReplied: number;
  niches: SourceNicheRow[];
  definitions: Definitions;
}

// ── GET /quality ────────────────────────────────────────────────────────────

export interface QualityResponse {
  window: InsightsWindow;
  proof: {
    liveUnreviewed: number;
    reviewedInWindow: number;
    ok: number;
    fail: number;
    unclear: number;
    coverage: Rate;
  };
  byOutreacher: {
    inviterId: number;
    name: string;
    reviewed: number;
    ok: number;
    fail: number;
    unclear: number;
    failRate: Rate;
  }[];
  signals: {
    /** Creators on more than one live (not released) valid invite: the headline. */
    duplicateCreatorsLive: number;
    /** The same across every valid invite, all time. */
    duplicateCreators: number;
    /** Matched on the outreacher's clipping account. Null if it can't be measured. */
    selfInvites: number | null;
    backdatedConversions: number;
    invitesWithoutMessage: number;
  };
  definitions: Definitions;
}
